/**
 * TTS Hooks
 * Consolidated hooks for text-to-speech functionality
 *
 * This module provides three complementary hooks:
 * - useSpeechSynthesis: Browser Speech Synthesis API access (generic, reusable)
 * - useTTSContent: Content state and preparation (component-level)
 * - useContentHighlighting: Highlighting calculations and smart scroll (rendering-level)
 *
 * Import as: import { useSpeechSynthesis, useTTSContent, useContentHighlighting } from '../../hooks'
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { calculateSentenceRanges, extractText } from '../lib/utils/tts'
import type { HighlightContext, TTSText, TTSSection } from '../lib/entities/TextToSpeech'
import { LocalContentDTO } from '@hazadapt-git/public-core-base'
import { TTS_CONFIG } from '../lib/entities'

// ============================================================================
// useSpeechSynthesis Hook
// ============================================================================

interface UseSpeechSynthesisReturn {
    /** Whether the browser supports Speech Synthesis API */
    supported: boolean
    /** Array of available voices */
    voices: SpeechSynthesisVoice[]
    /** Cancel current utterance */
    cancel: () => void
}

/**
 * Hook that provides Speech Synthesis API access
 * Returns: supported flag, available voices, and cancel function
 *
 * @returns Object with Speech Synthesis capabilities
 *
 * @example
 * const { supported, voices, cancel } = useSpeechSynthesis()
 *
 * if (!supported) {
 *   console.warn('Speech Synthesis not supported')
 *   return
 * }
 *
 * const speech = new SpeechSynthesisUtterance('Hello world')
 * speech.voice = voices[0]
 * window.speechSynthesis.speak(speech)
 */
export function useSpeechSynthesis(): UseSpeechSynthesisReturn {
    const [supported, setSupported] = useState(false)
    const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])

    // Check support and load voices on mount
    useEffect(() => {
        const isSupported =
            typeof window !== 'undefined' &&
            'speechSynthesis' in window &&
            'SpeechSynthesisUtterance' in window

        setSupported(isSupported)

        if (!isSupported) return

        // Load available voices
        const loadVoices = () => {
            const availableVoices = window.speechSynthesis.getVoices()
            setVoices(availableVoices)
        }

        // Get voices immediately (may be empty on first call in some browsers)
        loadVoices()

        // Listen for voiceschanged event (fires when voices are loaded)
        window.speechSynthesis.addEventListener('voiceschanged', loadVoices)

        return () => {
            window.speechSynthesis.removeEventListener('voiceschanged', loadVoices)
        }
    }, [])

    // Cancel function
    const cancel = useCallback(() => {
        if (supported && window.speechSynthesis) {
            window.speechSynthesis.cancel()
        }
    }, [supported])

    return { supported, voices, cancel }
}

// ============================================================================
// useTTSContent Hook
// ============================================================================

interface UseTTSContentProps {
    title: string
    header?: string
    body?: string
    localContent: LocalContentDTO[]
}

interface UseTTSContentReturn {
    ttsText: TTSText
    highlighted: {
        section: TTSSection
        index: number
    } | null
    handleSentenceChange: (
        info: { section: TTSSection; index: number } | null
    ) => void
}

/**
 * Hook to manage TTS content and highlighting state
 * Consolidates TTS setup logic into a single reusable hook
 *
 * @param props - Content to prepare for TTS
 * @returns Object containing ttsText, highlighted state, and sentence change handler
 *
 * @example
 * const { ttsText, highlighted, handleSentenceChange } = useTTSContent({
 *   title: "Hazard Title",
 *   header: "Optional header",
 *   body: "Main content...",
 *   localContent: []
 * })
 */
export function useTTSContent({
    title,
    header,
    body,
    localContent,
}: UseTTSContentProps): UseTTSContentReturn {
    // Gather all text content for TTS in sections
    const ttsText = useMemo(
        () => ({
            title: title || undefined,
            header: header || undefined,
            body: body || undefined,
            local:
                localContent?.map((lc) => lc.body).filter(Boolean) ||
                undefined,
        }),
        [title, header, body, localContent]
    )

    // Highlighted section/sentence for TTS
    const [highlighted, setHighlighted] = useState<{
        section: TTSSection
        index: number
    } | null>(null)

    const handleSentenceChange = useCallback(
        (info: { section: TTSSection; index: number } | null) => {
            setHighlighted(info)
        },
        []
    )

    return { ttsText, highlighted, handleSentenceChange }
}

// ============================================================================
// useContentHighlighting Hook
// ============================================================================

interface UseContentHighlightingProps {
    /** Full text content to analyze */
    content: string
    /** Index of sentence to highlight */
    highlightedSentenceIndex?: number
    /** CSS class name to apply for highlighting */
    highlightClassName: string
    /** CSS selector to find the highlighted element (for smart scroll) */
    highlightSelector?: string
    /** Threshold as fraction of viewport height for smart scroll */
    scrollThreshold?: number
    /** Scroll behavior: 'auto' or 'smooth' */
    scrollBehavior?: 'auto' | 'smooth'
    /** Scroll alignment: 'start', 'center', 'end', 'nearest' */
    scrollBlock?: 'start' | 'center' | 'end' | 'nearest'
}

interface UseContentHighlightingReturn {
    /** Context for highlighting calculations */
    highlightContext: HighlightContext
    /** Determines if an element should have the highlight class */
    getHighlightClass: (children: React.ReactNode) => string
}

/**
 * Hook for TTS highlighting and smart scroll behavior
 * Combines sentence range calculations with intelligent scrolling
 *
 * Smart scroll only triggers when the highlighted element is near the
 * bottom of the viewport, preventing jarring scroll behavior on each
 * sentence change while still keeping the highlighted text in view.
 *
 * @param props - Configuration for highlighting and scroll behavior
 * @returns Object with highlight context and class determination function
 *
 * @example
 * const { highlightContext, getHighlightClass } = useContentHighlighting({
 *   content: "Hello. World.",
 *   highlightedSentenceIndex: 0,
 *   highlightClassName: "highlighted",
 *   highlightSelector: ".highlighted",
 * })
 */
export function useContentHighlighting({
    content,
    highlightedSentenceIndex,
    highlightClassName,
    highlightSelector = `.${highlightClassName}`,
    scrollThreshold = TTS_CONFIG.SCROLL.viewportThreshold,
    scrollBehavior = TTS_CONFIG.SCROLL.behavior as 'auto' | 'smooth',
    scrollBlock = TTS_CONFIG.SCROLL.block as 'start' | 'center' | 'end' | 'nearest',
}: UseContentHighlightingProps): UseContentHighlightingReturn {
    // Calculate sentence ranges for highlighting
    const highlightContext = useMemo(() => {
        if (highlightedSentenceIndex === undefined) return {}

        const ranges = calculateSentenceRanges(content)

        return {
            highlightedSentenceIndex,
            sentenceRanges: ranges,
            fullText: content,
        }
    }, [content, highlightedSentenceIndex])

    // Determine if element text overlaps with currently highlighted sentence
    const getHighlightClass = (children: React.ReactNode): string => {
        if (
            !highlightContext.sentenceRanges ||
            highlightContext.highlightedSentenceIndex === undefined
        ) {
            return ''
        }

        const range =
            highlightContext.sentenceRanges[
                highlightContext.highlightedSentenceIndex
            ]
        if (!range) return ''

        // Extract text from React node (string, element, array, etc.)
        const elementText = extractText(children)

        // Find where this element's text starts in the full content
        // Using indexOf is safe here because we're looking for the first occurrence
        // in the remaining content (not at the start of full text)
        const elementStart =
            highlightContext.fullText?.indexOf(elementText) ?? -1
        if (elementStart === -1) return ''

        // Calculate element's character range in the full text
        const elementEnd = elementStart + elementText.length

        /**
         * RANGE OVERLAP DETECTION
         * Check if this element contains any characters from the highlighted range
         *
         * Two ranges overlap if:
         * - Element starts before highlight ends (elementStart < range.end)
         * - Element ends after highlight starts (elementEnd > range.start)
         *
         * Examples:
         * - Element [0,5], Highlight [3,7] -> Overlap (0<7 && 5>3)
         * - Element [0,2], Highlight [3,7] -> No overlap (0<7 but 2<3)
         * - Element [5,9], Highlight [3,7] -> Overlap (5<7 && 9>3)
         */
        if (elementStart < range.end && elementEnd > range.start) {
            return highlightClassName
        }

        return ''
    }

    // Smart scroll: only scroll when highlight is near bottom of viewport
    useEffect(() => {
        if (
            highlightedSentenceIndex === undefined ||
            highlightedSentenceIndex === null
        ) {
            return
        }

        // Find the currently highlighted element using the selector
        const highlightedElement = document.querySelector(
            highlightSelector
        ) as HTMLElement
        if (!highlightedElement) return

        // Get element's position relative to viewport
        const rect = highlightedElement.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        /**
         * SMART SCROLL THRESHOLD
         * Only scroll if element is in the bottom portion of the viewport
         *
         * This prevents jarring scroll behavior where every highlight move
         * causes the page to scroll. Instead, we let the user read through
         * content naturally and only scroll when they're about to scroll off-screen.
         *
         * scrollThreshold: fraction of viewport height (0-1)
         * Default 0.8 means: scroll if element is in the bottom 20% of viewport
         *
         * Example with window height 1000px:
         * - threshold = 0.8 → trigger at 800px from top
         * - If element is at 700px: don't scroll (still have space)
         * - If element is at 850px: scroll (about to leave screen)
         */
        if (rect.bottom > viewportHeight * scrollThreshold) {
            highlightedElement.scrollIntoView({
                behavior: scrollBehavior,
                block: scrollBlock,
            })
        }
    }, [
        highlightedSentenceIndex,
        highlightSelector,
        scrollThreshold,
        scrollBehavior,
        scrollBlock,
    ])

    return { highlightContext, getHighlightClass }
}
